import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import OcRequestStatusBadge from '../../components/OrganoidRequest/OcRequestStatusBadge';
import OcWorkflowTimeline from '../../components/OrganoidRequest/OcWorkflowTimeline';
import OcCommentsPanel from '../../components/OrganoidRequest/OcCommentsPanel';
import OcAttachmentsPanel from '../../components/OrganoidRequest/OcAttachmentsPanel';
import OcTransitionBar from '../../components/OrganoidRequest/OcTransitionBar';
import AssigneeUserSelect from '../../components/OrganoidRequest/AssigneeUserSelect';
import OcStageNav from '../../components/OrganoidRequest/OcStageNav';
import OcRequestCriteriaSummary from '../../components/OrganoidRequest/OcRequestCriteriaSummary';
import OcWorkspaceShell from '../../components/OrganoidRequest/OcWorkspaceShell';
import {
  OC_ASSIGNEE_ROLE_LABELS,
  OC_SAMPLE_CLASSIFICATIONS,
  formatWaitingOnLabel,
  getAssigneeIdForRole,
  getOcStageLabel,
  getPrimaryTransitionForPage,
  seedSendClientConfirmation,
} from '../../utils/ocRequest';
import { useOcRequestWorkspace } from '../../utils/useOcRequestWorkspace';
import { showToast } from '../../config_and_helpers/helpers';
import { logInfo, logError } from '../../config_and_helpers/logger';

const LOG_CONTEXT = 'OcRequestDetailPage';

const ASSIGNEE_ROLES = ['admin', 'data_manager', 'technician'];

/**
 * Detail + audit at /organoid-requests/:id or /organoid-requests/access/:labToken.
 * Stage assignees auto-save; admin fields persist with the Complete transition.
 */
const OcRequestDetailPage = () => {
  const { api, requestId, basePath, showInboxLink, labAccess, mode, readOnly } =
    useOcRequestWorkspace();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const [assigneeFields, setAssigneeFields] = useState({
    assignee_admin: null,
    assignee_data_manager: null,
    assignee_technician: null,
  });
  const [savingAssignees, setSavingAssignees] = useState(false);
  const [adminFields, setAdminFields] = useState({
    sample_classification: 'unknown',
    agreements_available: false,
    pricing_required: false,
    send_client_confirmation: true,
    admin_notes: '',
  });

  const seedAssignees = useCallback(request => {
    setAssigneeFields({
      assignee_admin: getAssigneeIdForRole(request, 'admin'),
      assignee_data_manager: getAssigneeIdForRole(request, 'data_manager'),
      assignee_technician: getAssigneeIdForRole(request, 'technician'),
    });
  }, []);

  const setLabAccessRequest = labAccess?.setRequest;

  const applyDetail = useCallback(
    request => {
      setData(request);
      seedAssignees(request);
      setAdminFields({
        sample_classification: request.sample_classification || 'unknown',
        agreements_available: Boolean(request.agreements_available),
        pricing_required: Boolean(request.pricing_required),
        send_client_confirmation: seedSendClientConfirmation(
          request.send_client_confirmation
        ),
        admin_notes: request.admin_notes || '',
      });
      if (setLabAccessRequest) setLabAccessRequest(request);
    },
    [setLabAccessRequest, seedAssignees]
  );

  const load = useCallback(async () => {
    if (!api) return;
    setLoading(true);
    const [result, candidatesResult] = await Promise.all([
      api.getDetail(),
      api.getAssigneeCandidates(),
    ]);
    setLoading(false);
    if (!result.success) {
      showToast(result.messages?.[0] || 'Failed to load request', 'error', 'top-right', 4000);
      return;
    }
    applyDetail(result.data);
    if (candidatesResult.success) {
      setCandidates(candidatesResult.data || []);
    }
    logInfo(LOG_CONTEXT, 'detail loaded', { requestId, mode });
  }, [api, requestId, mode, applyDetail]);

  useEffect(() => {
    let cancelled = false;
    if (mode === 'labPublic' && labAccess?.request) {
      applyDetail(labAccess.request);
      setLoading(false);
      api?.getAssigneeCandidates().then(candidatesResult => {
        if (cancelled) return;
        if (candidatesResult.success) {
          setCandidates(candidatesResult.data || []);
        }
      });
      return () => {
        cancelled = true;
      };
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [load, mode, applyDetail, api, labAccess?.request]);

  const persistAssignees = async nextFields => {
    setSavingAssignees(true);
    const result = await api.patchRequest({
      assignee_admin: nextFields.assignee_admin,
      assignee_data_manager: nextFields.assignee_data_manager,
      assignee_technician: nextFields.assignee_technician,
    });
    setSavingAssignees(false);
    if (result.success) {
      applyDetail(result.data);
      logInfo(LOG_CONTEXT, 'assignees auto-saved', { requestId, mode });
    } else {
      logError(LOG_CONTEXT, 'assignee auto-save failed', {
        requestId,
        messages: result.messages,
      });
      showToast(result.messages?.[0] || 'Could not save assignees', 'error', 'top-right', 4000);
    }
  };

  const onAssigneeChange = (field, userId) => {
    const nextFields = { ...assigneeFields, [field]: userId };
    setAssigneeFields(nextFields);
    persistAssignees(nextFields);
  };

  /**
   * Persist admin review fields (and current assignees) before any transition.
   * No separate Save admin / Save assignees buttons.
   */
  const beforeTransition = async () => {
    const result = await api.patchRequest({
      ...adminFields,
      assignee_admin: assigneeFields.assignee_admin,
      assignee_data_manager: assigneeFields.assignee_data_manager,
      assignee_technician: assigneeFields.assignee_technician,
    });
    if (!result.success) {
      logError(LOG_CONTEXT, 'pre-transition patch failed', {
        requestId,
        messages: result.messages,
      });
      showToast(
        result.messages?.[0] || 'Could not save admin fields before completing',
        'error',
        'top-right',
        5000
      );
      return false;
    }
    applyDetail(result.data);
    logInfo(LOG_CONTEXT, 'admin + assignees patched before transition', {
      requestId,
      mode,
      send_client_confirmation: adminFields.send_client_confirmation,
    });
    return true;
  };

  const downloadPdf = async () => {
    const result = await api.exportPdf();
    if (!result.success) {
      showToast(result.messages?.[0] || 'Export failed', 'error', 'top-right', 4000);
      return;
    }
    const url = window.URL.createObjectURL(result.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${data?.request_number || 'oc-request'}.pdf`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading || !data || !api) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  const primary = getPrimaryTransitionForPage('detail', data.status);
  const secondaryActions = [
    {
      action: 'request_client_clarification',
      label: 'Request clarification',
      variant: 'outline-warning',
    },
    {
      action: 'cancel_request',
      label: 'Cancel',
      variant: 'outline-danger',
    },
    {
      action: 'revoke_client_token',
      label: 'Revoke client token',
      variant: 'outline-dark',
    },
  ];
  if (data.status === 'TECHNICAL_REVIEW_FAILED') {
    secondaryActions.unshift(
      {
        action: 'return_to_data_manager',
        label: 'Return to data manager',
        variant: 'outline-warning',
      },
      {
        action: 'return_to_technician',
        label: 'Return to technician',
        variant: 'outline-warning',
      },
      {
        action: 'reject_request',
        label: 'Reject request',
        variant: 'outline-danger',
      }
    );
  }
  const content = (
    <>
      {showInboxLink ? (
        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
          <Button as={Link} to="/organoid-requests" variant="link" className="px-0">
            ← All requests
          </Button>
        </div>
      ) : null}
      <div className="oc-page-heading">
        <h2 className="mb-0">{data.request_number}</h2>
        <OcRequestStatusBadge status={data.status} />
        <span className="text-muted">{getOcStageLabel(data.current_stage)}</span>
        <Button size="sm" variant="outline-secondary" onClick={downloadPdf}>
          Export PDF
        </Button>
      </div>

      <OcStageNav basePath={basePath} active="detail" />

      <Row className="g-3">
        <Col lg={8}>
          <Card className="oc-glass-panel mb-3 border-0">
            <Card.Header>Request & criteria</Card.Header>
            <Card.Body>
              <OcRequestCriteriaSummary request={data} />
            </Card.Body>
          </Card>

          <Card className="oc-glass-panel mb-3 border-0">
            <Card.Header>Admin fields</Card.Header>
            <Card.Body>
              <p className="text-muted small mb-2">
                {readOnly
                  ? 'This account is read-only, so these fields cannot be changed.'
                  : 'Saved automatically when you Complete / send to data review.'}
              </p>
              <fieldset disabled={readOnly} className="border-0 p-0 m-0">
              <Row className="g-2">
                <Col md={4}>
                  <Form.Select
                    value={adminFields.sample_classification}
                    onChange={e =>
                      setAdminFields(prev => ({
                        ...prev,
                        sample_classification: e.target.value,
                      }))
                    }
                  >
                    {OC_SAMPLE_CLASSIFICATIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={4}>
                  <Form.Check
                    type="checkbox"
                    label="Agreements available"
                    checked={adminFields.agreements_available}
                    onChange={e =>
                      setAdminFields(prev => ({
                        ...prev,
                        agreements_available: e.target.checked,
                      }))
                    }
                  />
                </Col>
                <Col md={4}>
                  <Form.Check
                    type="checkbox"
                    label="Pricing required"
                    checked={adminFields.pricing_required}
                    onChange={e =>
                      setAdminFields(prev => ({
                        ...prev,
                        pricing_required: e.target.checked,
                      }))
                    }
                  />
                </Col>
                <Col md={12}>
                  <Form.Check
                    type="checkbox"
                    label="Send confirmation email to the client"
                    checked={adminFields.send_client_confirmation}
                    onChange={e =>
                      setAdminFields(prev => ({
                        ...prev,
                        send_client_confirmation: e.target.checked,
                      }))
                    }
                  />
                  <p className="text-muted small mb-0 mt-1">
                    Uncheck to skip the client email after technical review. Approved
                    OCs go straight to preparation.
                  </p>
                </Col>
                <Col md={12}>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Admin notes (internal)"
                    value={adminFields.admin_notes}
                    onChange={e =>
                      setAdminFields(prev => ({ ...prev, admin_notes: e.target.value }))
                    }
                  />
                </Col>
              </Row>
              </fieldset>
            </Card.Body>
          </Card>

          <Card className="oc-glass-panel mb-3 border-0">
            <Card.Header>Stage assignees</Card.Header>
            <Card.Body>
              <div className="mb-2">
                <strong>Currently responsible:</strong>{' '}
                {data.assigned_to_username || data.assigned_to || '—'}
              </div>
              <div className="mb-3 text-muted small">
                Waiting on: {formatWaitingOnLabel(data)}
                {savingAssignees ? ' · Saving…' : ''}
              </div>
              <p className="text-muted small mb-2">
                Assigning here is enough for handoff emails — no separate Assign next
                step.
              </p>
              {ASSIGNEE_ROLES.map(role => {
                const field = `assignee_${role}`;
                return (
                  <AssigneeUserSelect
                    key={role}
                    controlId={`oc-detail-${field}`}
                    label={OC_ASSIGNEE_ROLE_LABELS[role]}
                    options={candidates}
                    value={assigneeFields[field]}
                    onChange={userId => onAssigneeChange(field, userId)}
                    disabled={readOnly || savingAssignees}
                  />
                );
              })}
            </Card.Body>
          </Card>

          {!readOnly ? (
          <OcTransitionBar
            api={api}
            requestId={requestId}
            request={data}
            action={primary?.action}
            label={primary?.label || 'Complete'}
            beforeTransition={beforeTransition}
            secondaryActions={secondaryActions}
            onSuccess={updated => {
              applyDetail(updated);
            }}
          />
          ) : null}
        </Col>
        <Col lg={4}>
          <Card className="oc-glass-panel mb-3 border-0">
            <Card.Header>Audit timeline</Card.Header>
            <Card.Body>
              <OcWorkflowTimeline actions={data.workflow_actions || []} />
            </Card.Body>
          </Card>
          <Card className="oc-glass-panel mb-3 border-0">
            <Card.Body>
              <OcCommentsPanel
                api={api}
                comments={data.comments || []}
                allowCompose={!readOnly}
                onUpdated={() => load()}
              />
            </Card.Body>
          </Card>
          <Card className="oc-glass-panel border-0">
            <Card.Body>
              <OcAttachmentsPanel
                api={api}
                attachments={data.attachments || []}
                allowUpload={!readOnly}
                onUpdated={() => load()}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );

  if (mode === 'labPublic') {
    return content;
  }

  return <OcWorkspaceShell wide theme="yellow">{content}</OcWorkspaceShell>;
};

export default OcRequestDetailPage;
